import * as React from "react";
import styled, { css } from "styled-components";
import { select, Colorable, ColorType } from "@hubper/theme";
import Icon, { IconName } from "../../elements/Icon";

import { containerStyle, ContainerProps } from "../../elements/Button";
import Text from "../../elements/Text";

const ARROW_BOX_WIDTH = 28;
const HEIGHT = 40;

const ArrowBox = styled.div<{ isOpen: boolean }>`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  right: 0;
  top: 0;
  bottom: 0;
  width: ${ARROW_BOX_WIDTH}px;
  background-color: rgba(0, 0, 0, 0.2);
  color: white;

  & > * {
    transition: 0.2s;
    transform: rotate(${p => (p.isOpen ? 180 : 0)}deg);
  }
`;

const activeStyle = css`
  background-color: ${select.findColorAlterIndex(1)};

  & ${ArrowBox} {
    background-color: rgba(0, 0, 0, 0.3);
  }
`;

const Base = styled.button<Colorable & ContainerProps & { isOpen: boolean }>`
  ${containerStyle}
  ${p =>
    !p.square
      ? css`
          padding: 0rem ${select.space(4)};
          padding-right: calc(${select.space(3)} + ${ARROW_BOX_WIDTH}px);
        `
      : css`
          width: ${HEIGHT + ARROW_BOX_WIDTH}px;
          padding-right: ${HEIGHT}px;
        `}
  position: relative;
  height: ${HEIGHT}px;
  background-color: ${select.findColor};
  overflow: hidden;

  & ${Text} {
    color: white;
  }

  color: white;

  ${p => p.isOpen && activeStyle}

  ${p =>
    p.isOpen &&
    css`
      border-bottom-left-radius: 0px;
      border-bottom-right-radius: 0px;
    `}

  ${p =>
    !p.disabled &&
    css`
      &:hover {
        ${activeStyle}
      }
    `}
`;

Base.defaultProps = {
  c: ["primary", 5]
};

export type ButtonProps = {
  text?: string;
  iconBefore?: IconName;
  iconAfter?: IconName;
  icon?: IconName;
  stretch?: boolean;
  c?: string | ColorType;
  disabled?: boolean;
  isOpen?: boolean;
} & React.HTMLAttributes<HTMLButtonElement>;

export const Button = React.forwardRef((props: ButtonProps, ref: any) => {
  const {
    icon,
    iconAfter,
    iconBefore,
    text,
    c,
    stretch,
    disabled,
    isOpen,
    ...nativeButtonProps
  } = props;

  function handleClick(evt: any) {
    if (disabled || !nativeButtonProps.onClick) {
      return;
    }
    nativeButtonProps.onClick(evt);
  }

  // @ts-ignore
  const hasIcon = icon && icon !== "none";

  let innerContent = null;
  if (hasIcon) {
    innerContent = <Icon data-testid="button-icon" name={icon as any} />;
  } else {
    innerContent = (
      <>
        {iconBefore && (
          <Icon data-testid="button-icon-before" name={iconBefore} />
        )}
        <Text
          data-testid="button-text"
          fs={-1}
          fw={"bold"}
          c="inherit"
          style={{ lineHeight: 1 }}
        >
          {text}
        </Text>
        {iconAfter && <Icon data-testid="button-icon-after" name={iconAfter} />}
      </>
    );
  }

  return (
    <Base
      c={c}
      ref={ref}
      square={Boolean(hasIcon)}
      stretch={stretch!}
      cursor={nativeButtonProps.onClick && !disabled ? "pointer" : "default"}
      disabled={disabled}
      {...nativeButtonProps}
      onClick={handleClick}
      data-testid="dropdown-bottom"
      isOpen={isOpen!}
    >
      {innerContent}
      <ArrowBox isOpen={isOpen!}>
        <Icon name="dropdown" />
      </ArrowBox>
    </Base>
  );
});

export default Button;
